import React from 'react';

const MalaysiaIndonesiaAIGov = () => (
  <article className="news-article-body">
    <p className="news-lead">
      While Vietnam became the first SEA country with binding AI law, <strong>Malaysia</strong>,
      <strong> Indonesia</strong>, <strong>the Philippines</strong>, and <strong>Thailand</strong>
      are each building their own governance architectures — at different speeds and with
      different priorities. Together, these four countries represent over 500 million people
      and some of the fastest-growing digital economies in the region.
    </p>

    <h2>Malaysia — National AI Office and the AIGE Framework</h2>
    <p>
      Malaysia's AI governance is coordinated by the <strong>National AI Office (NAIO)</strong>,
      fully operationalised in 2025 under MyDIGITAL Corporation. NAIO serves as the central
      body for AI strategy, standard-setting, and cross-ministry coordination.
    </p>

    <h3>National Guidelines on AI Governance and Ethics (AIGE)</h3>
    <p>
      Published by the Ministry of Science, Technology and Innovation (MOSTI) in September 2024,
      AIGE is Malaysia's voluntary AI ethics framework built on seven principles: fairness,
      reliability and safety, privacy and security, inclusiveness, transparency, accountability,
      and human-centricity. It provides targeted guidance for three groups:
    </p>
    <ul>
      <li><strong>End users</strong>: How to use AI responsibly and recognise AI-generated content</li>
      <li><strong>Policymakers</strong>: Procurement standards, public sector deployment guidelines</li>
      <li><strong>Developers</strong>: Model documentation, bias testing, safety evaluation requirements</li>
    </ul>

    <h3>AI Technology Action Plan 2026–2030</h3>
    <p>
      NAIO is developing a five-year roadmap translating policy into implementation milestones.
      It proposes a <strong>risk-based regulatory framework</strong> categorising AI applications
      by harm potential — high-risk uses (biometric ID, clinical decision support, credit
      scoring, safety-critical infrastructure) face stricter documentation and testing
      requirements, aligned with Malaysia's updated Cybersecurity Act 2024 and amended
      Personal Data Protection Act 2024.
    </p>
    <p>
      Malaysia's ILMU model (see dedicated article) also has direct governance implications:
      by hosting sovereign AI compute within Malaysian borders, YTL and NAIO are building
      the infrastructure for data-sovereign AI deployment that AIGE recommends.
    </p>

    <h2>Indonesia — Presidential Regulations and the UU PDP</h2>
    <p>
      Indonesia's approach to AI governance is anchored in presidential authority rather
      than parliamentary legislation — reflecting the country's executive-led digital
      transformation strategy.
    </p>

    <h3>Two Presidential Regulations Coming in 2026</h3>
    <p>
      The Ministry of Communication and Digital Affairs is finalising two parallel
      Perpres (Presidential Regulations):
    </p>
    <ul>
      <li>
        <strong>Presidential Regulation on AI Ethics</strong>: Defines permissible values,
        benefits, and protective measures for AI deployment in Indonesia. Applies to
        government systems and commercial deployments affecting Indonesian citizens.
      </li>
      <li>
        <strong>Presidential Regulation on the National AI Roadmap 2026–2030</strong>:
        Sets strategy for AI ecosystem development aligned with five national priority
        sectors — healthcare, bureaucratic reform, education, food security, and smart cities.
      </li>
    </ul>

    <h3>Personal Data Protection Law (UU PDP)</h3>
    <p>
      Effective October 17, 2024, Indonesia's <strong>UU PDP</strong> is modelled on GDPR
      and directly governs AI systems that process personal data. Key requirements:
    </p>
    <ul>
      <li>Explicit consent required for using personal data in AI training datasets</li>
      <li>Data Protection Officers mandated for high-volume personal data processors</li>
      <li>Breach notification obligations within specified timeframes</li>
      <li>A dedicated UU PDP enforcement agency planned for launch in 2026</li>
      <li>Penalties: up to IDR 2 billion or 2% of annual revenue for violations</li>
    </ul>
    <p>
      Indonesia's "Digital Sovereignty" framing — aligned with Pancasila values — means
      AI governance is explicitly connected to national identity and cultural values,
      not just technical risk management. This shapes how AI bias, fairness, and
      representativeness are evaluated in Indonesian regulatory contexts.
    </p>

    <h2>Philippines — National AI Strategy 2025–2028</h2>
    <p>
      President Ferdinand Marcos Jr. approved the <strong>National AI Strategy for the
      Philippines (NAIS-PH)</strong> in May 2025 — a whole-of-government framework through
      2028 built on five pillars:
    </p>
    <div className="news-specs-grid">
      <div className="news-spec-item">
        <span className="news-spec-label">Infrastructure</span>
        <span className="news-spec-value">Cloud compute access, connectivity, data centres</span>
      </div>
      <div className="news-spec-item">
        <span className="news-spec-label">Workforce</span>
        <span className="news-spec-value">AI literacy, retraining, digital skills at scale</span>
      </div>
      <div className="news-spec-item">
        <span className="news-spec-label">Innovation Ecosystem</span>
        <span className="news-spec-value">R&D funding, startup support, academic partnerships</span>
      </div>
      <div className="news-spec-item">
        <span className="news-spec-label">Ethical Policy</span>
        <span className="news-spec-value">Human-centric AI principles, accountability frameworks</span>
      </div>
      <div className="news-spec-item">
        <span className="news-spec-label">Priority Deployment</span>
        <span className="news-spec-value">Healthcare, agriculture, disaster response, education</span>
      </div>
    </div>
    <p>
      Current governance relies on soft law and the Data Privacy Act 2012, with
      sector-specific rules including COMELEC's restrictions on deepfakes in election
      campaigns. A comprehensive <strong>AI Development Act</strong> remains under
      legislative review — likely to pass by 2027.
    </p>

    <h2>Thailand — From Guidelines to Statute</h2>
    <p>
      Thailand is transitioning from guideline-based AI governance to a formal statutory
      framework. Key developments:
    </p>
    <ul>
      <li>
        <strong>Draft AI Law 2025</strong>: Expected to formalise in 2026, establishing
        a risk-based classification system aligned with OECD AI Principles and the EU AI Act.
        Three risk tiers: prohibited, high-risk, and minimal-risk.
      </li>
      <li>
        <strong>AI Governance Center (AIGC)</strong>: Established within ETDA (Electronic
        Transactions Development Agency) to provide technical standards, testing certification,
        and regulatory sandbox operations.
      </li>
      <li>
        <strong>Bank of Thailand AI Risk Guidelines (Sep 2025)</strong>: Sector-specific
        guidance for financial institutions deploying AI in credit assessment, fraud detection,
        and customer service — requiring explainability, fairness testing, and model risk
        management documentation.
      </li>
    </ul>

    <div className="news-insight-box">
      <h3>Key Takeaway</h3>
      <p>
        The governance landscape across Malaysia, Indonesia, Philippines, and Thailand
        reflects a region moving at different speeds toward the same destination: a
        risk-proportionate AI regulatory framework. The practical implication for
        enterprises operating across SEA is to build AI governance processes to
        the highest applicable standard — currently Vietnam's new law and Singapore's
        agentic AI framework — as these will cascade into binding requirements
        across the region through 2026–2028.
      </p>
    </div>
  </article>
);

export default MalaysiaIndonesiaAIGov;
